import React, { useState, useEffect } from "react";
import { instagramService, InstagramPost } from "../services/instagram/instagramService";
import instagramConfig from "../config/instagram";

interface InstagramProps {
  limit?: number;
  username?: string;
}

interface DisplayPost {
  url: string;
  shortcode: string;
  thumbnail?: string;
  isVideo?: boolean;
}

// Convert API post to display format
const convertToDisplayPost = (post: InstagramPost): DisplayPost => ({
  url: post.permalink,
  shortcode: post.id,
  thumbnail: post.media_type === 'VIDEO' ? post.thumbnail_url : post.media_url,
  isVideo: post.media_type === 'VIDEO' || post.media_type === 'CAROUSEL_ALBUM'
});

export const Instagram: React.FC<InstagramProps> = ({ 
  limit = instagramConfig.postsLimit,
  username = instagramConfig.username
}) => {
  const [posts, setPosts] = useState<DisplayPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch posts from Instagram API
  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      setError(null);

      if (!instagramService.isConfigured()) {
        console.error('Instagram API not configured. Please add credentials to .env file');
        setError('Instagram API no está configurado');
        setLoading(false);
        return;
      }

      try {
        console.log('Fetching Instagram posts from API...');
        const apiPosts = await instagramService.getLatestPosts(limit);
        
        if (apiPosts && apiPosts.length > 0) {
          const displayPosts = apiPosts.map(convertToDisplayPost);
          setPosts(displayPosts);
          console.log(`✅ Loaded ${apiPosts.length} posts from Instagram API`);
        } else {
          console.warn('No posts returned from API');
          setError('No se encontraron publicaciones');
        }
      } catch (err) {
        console.error('Failed to fetch from Instagram API:', err);
        setError('No se pudieron cargar las publicaciones de Instagram');
      }

      setLoading(false);
    };

    fetchPosts();
  }, [limit]);

  const profileUrl = `https://www.instagram.com/${username}`;

  // Don't render anything while loading
  if (loading) {
    return null;
  }

  // Don't render anything if there's an error or no posts
  if (error || posts.length === 0) {
    console.warn('Instagram component hidden: API not working or no posts available');
    return null;
  }

  return (
    <div id="instagram" className="instagram-modern-section">
      <div className="instagram-modern-container">
        <div className="instagram-modern-header">
          <h2 className="instagram-modern-title">{instagramConfig.sectionTitle}</h2>
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="instagram-modern-username"
          >
            @{username}
          </a>
        </div>

        <div className="instagram-modern-grid">
          {posts.slice(0, limit).map((post) => (
            <a
              key={post.shortcode}
              href={post.url}
              target="_blank"
              rel="noopener noreferrer"
              className="instagram-modern-post"
            >
              <div className="instagram-modern-post-inner">
                <img
                  src={post.thumbnail || `https://www.instagram.com/p/${post.shortcode}/media/?size=l`}
                  alt="Instagram post"
                  className="instagram-modern-image"
                  loading="lazy"
                />
                {post.isVideo && (
                  <div className="instagram-modern-play-icon">
                    <svg viewBox="0 0 24 24" fill="white" width="40" height="40">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                )}
                <div className="instagram-modern-overlay">
                  <svg viewBox="0 0 24 24" fill="white" width="32" height="32">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
              </div>
            </a>
          ))}
        </div>

        <div className="instagram-modern-cta">
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="instagram-modern-button"
          >
            Ver más en Instagram
          </a>
        </div>
      </div>
    </div>
  );
};

